import { useEnrolledProducts } from '~/hooks/useEnrolledProducts';
import { PLATFORM_PRODUCT_CODES } from '~/store/trial.store';
import type { TrialPlatform } from '~/store/trial.store';
import { isFutureIso, isPastIso } from '~/utils/date';

/** Temporal + contract state of a platform's trial window. */
export type TrialContractStatus = 'loading' | 'not_started' | 'active' | 'expired' | 'not_enrolled';

export interface TrialContractStatusResult {
  status: TrialContractStatus;
  pocStartDate: string | null;
  pocEndDate: string | null;
}

/**
 * Derives whether a platform's TRIAL_POC contract hasn't started yet, is
 * active, or has expired, from `pocStartDate`/`pocEndDate` on the enrolled
 * product. Reads the same enrolled-products query cache that
 * `requireContract` (src/utils/guards.ts) already primes, so no extra fetch
 * or store is needed.
 */
export function useTrialContractStatus(platform: TrialPlatform): TrialContractStatusResult {
  const { data: products } = useEnrolledProducts();

  if (products === undefined) {
    return { status: 'loading', pocStartDate: null, pocEndDate: null };
  }

  const product = products.find((p) => p.productCode === PLATFORM_PRODUCT_CODES[platform]);
  if (product?.contractType !== 'TRIAL_POC') {
    return { status: 'not_enrolled', pocStartDate: null, pocEndDate: null };
  }

  const { pocStartDate, pocEndDate } = product;
  if (pocStartDate && isFutureIso(pocStartDate)) {
    return { status: 'not_started', pocStartDate, pocEndDate };
  }
  if (pocEndDate && isPastIso(pocEndDate)) {
    return { status: 'expired', pocStartDate, pocEndDate };
  }
  return { status: 'active', pocStartDate, pocEndDate };
}
